import getIn from 'lodash/get';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import uniqWith from 'lodash/uniqWith';

import { EvaluationResult } from '@evidentid/tprm-portal-lib/models/entity-details';
import {
    EntityRequirementDetails,
} from '@evidentid/tprm-portal-lib/models/entity-details/EntityRequirement.model';

import { CategorizedEnumLabels } from '@/modules/dashboard/models/CategorizedEnumLabels.model';
import { FieldEvaluationResultError } from '@/modules/decisioning-criteria/types';
import { FIELD_NOT_AVAILABLE_MESSAGE } from '@/modules/entity-details/constant';
import { EntityMultiFieldCriterion, EntityMultiFieldCriterionField } from '@/modules/entity-details/types';
import { getEnumDefaultDisplayValue } from '@/utils/getEnumDefaultDisplayValue';

export function getMultiFieldCriteria(
    evaluationResults: EvaluationResult[],
    evaluationErrors: Record<string, FieldEvaluationResultError>,
    requirementDetailsList: EntityRequirementDetails[],
    categorizedEnumLabels: CategorizedEnumLabels,
): EntityMultiFieldCriterion[] {
    const multiFieldCriteria = evaluationResults.reduce((acc, evaluationResult) => {
        if (evaluationResult.usedFields.length > 1) {
            const entityMultiFieldCriterion: EntityMultiFieldCriterion = {
                name: evaluationResult.criterionName,
                fields: evaluationResult.usedFields.reduce((fields, field) => {
                    const parts = field.split('/');
                    const coverageName = parts.at(1);
                    const name = parts.at(-1);
                    const matchedRequirement = requirementDetailsList
                        .find((details) => coverageName === details.coverage?.coverageType);
                    const requirementDetails = matchedRequirement?.coverage?.details;
                    if (name) {
                        const value = getIn(requirementDetails, parts.slice(3), null);
                        // Keyed by the full field path (not just `name`) so that fields with
                        // the same name from different coverage types (e.g. a risk
                        // requirement's limit and Umbrella's limit) don't overwrite each other.
                        fields[field] = {
                            name,
                            value: !isNil(value) ?
                                getEnumDefaultDisplayValue(name, value, categorizedEnumLabels) :
                                FIELD_NOT_AVAILABLE_MESSAGE,
                            coverageType: coverageName ?? '',
                            // The source policy's own currency, not the rendered requirement's —
                            // a field pulled in from another coverage (e.g. Umbrella) may belong
                            // to a differently-currencied policy.
                            currency: matchedRequirement?.coverage?.policy?.currency ?? '',
                        };
                    }
                    return fields;
                }, {} as Record<string, EntityMultiFieldCriterionField>),
                evaluationError: evaluationErrors[evaluationResult.criterionName],
            };
            acc.push(entityMultiFieldCriterion);
        }

        return acc;
    }, [] as EntityMultiFieldCriterion[]);
    return uniqWith(multiFieldCriteria, isEqual);
}
